import { useCallback, useMemo, useState } from 'react';
import { useSignIn, useSignUp } from '@clerk/clerk-react';

export type GoogleOAuthMode = 'sign-in' | 'sign-up';

export type GoogleOAuthRedirectOptions = {
  strategy: 'oauth_google';
  redirectUrl: string;
  redirectUrlComplete: string;
  oidcPrompt?: 'select_account';
  continueSignIn?: boolean;
  continueSignUp?: boolean;
};

type GoogleOAuthButtonProps = {
  language: 'es' | 'en';
  mode: GoogleOAuthMode;
  oauthMode?: GoogleOAuthMode;
  redirectUrlComplete: string;
  className?: string;
  forceAccountSelection?: boolean;
  /** @deprecated Cross-mode continuation is intentionally ignored because Clerk rejects it for this OAuth flow. */
  allowCrossModeCompletion?: boolean;
};

type ExternalVerification = {
  externalVerificationRedirectURL?: URL | string | null;
};

type OAuthAttemptResult = {
  firstFactorVerification?: ExternalVerification | null;
  verifications?: {
    externalAccount?: ExternalVerification | null;
  } | null;
};

type LegacyOAuthResource = {
  create(params: {
    strategy: 'oauth_google';
    redirectUrl: string;
    actionCompleteRedirectUrl: string;
    oidcPrompt?: 'select_account';
  }): Promise<OAuthAttemptResult>;
};

const SSO_CALLBACK_PATH = '/sso-callback';
export const OAUTH_REDIRECT_INTENT_STORAGE_KEY = 'innerbloom:oauth-redirect-intent';
const OAUTH_REDIRECT_INTENT_TTL_MS = 10 * 60 * 1000;

export type OAuthRedirectIntent = {
  mode: GoogleOAuthMode;
  redirectUrlComplete: string;
  createdAt: number;
};

export function buildGoogleOAuthRedirectOptions(
  redirectUrlComplete: string,
  forceAccountSelection: boolean,
): GoogleOAuthRedirectOptions {
  return {
    strategy: 'oauth_google',
    redirectUrl: SSO_CALLBACK_PATH,
    redirectUrlComplete,
    oidcPrompt: forceAccountSelection ? 'select_account' : undefined,
    continueSignIn: false,
    continueSignUp: false,
  };
}

export function forceGoogleAccountChooserUrl(value: URL | string): string {
  const url = new URL(value.toString());
  const hostname = url.hostname.toLowerCase();
  if (hostname !== 'accounts.google.com') {
    throw new Error(`Unexpected Google OAuth host: ${hostname || 'missing'}`);
  }

  url.searchParams.set('prompt', 'select_account');
  url.searchParams.delete('authuser');
  return url.toString();
}

function getExternalVerificationUrl(attempt: OAuthAttemptResult): URL | string | null {
  return attempt.firstFactorVerification?.externalVerificationRedirectURL
    ?? attempt.verifications?.externalAccount?.externalVerificationRedirectURL
    ?? null;
}

export async function startGoogleOAuthRedirect(
  resource: LegacyOAuthResource,
  redirectUrlComplete: string,
  forceAccountSelection: boolean,
): Promise<void> {
  const attempt = await resource.create({
    strategy: 'oauth_google',
    redirectUrl: SSO_CALLBACK_PATH,
    actionCompleteRedirectUrl: redirectUrlComplete,
    oidcPrompt: forceAccountSelection ? 'select_account' : undefined,
  });
  const externalVerificationUrl = getExternalVerificationUrl(attempt);
  if (!externalVerificationUrl) {
    throw new Error('Clerk did not return an external Google verification URL');
  }

  const targetUrl = forceAccountSelection
    ? forceGoogleAccountChooserUrl(externalVerificationUrl)
    : externalVerificationUrl.toString();

  console.info('[auth] navigating to Google OAuth', {
    accountSelection: forceAccountSelection ? 'required' : 'default',
    hasSelectAccountPrompt: new URL(targetUrl).searchParams.get('prompt') === 'select_account',
  });
  window.location.assign(targetUrl);
}

export function describeClerkOAuthError(error: unknown): Record<string, unknown> {
  const candidate = error && typeof error === 'object'
    ? error as {
        name?: unknown;
        message?: unknown;
        code?: unknown;
        errors?: Array<{ code?: unknown; message?: unknown; longMessage?: unknown }>;
      }
    : null;

  return {
    name: error instanceof Error ? error.name : typeof candidate?.name === 'string' ? candidate.name : null,
    message: error instanceof Error
      ? error.message
      : typeof candidate?.message === 'string'
        ? candidate.message
        : typeof error === 'string'
          ? error
          : String(error),
    code: typeof candidate?.code === 'string' ? candidate.code : null,
    errors: Array.isArray(candidate?.errors)
      ? candidate.errors.map((item) => ({
          code: typeof item?.code === 'string' ? item.code : null,
          message: typeof item?.message === 'string' ? item.message : null,
          longMessage: typeof item?.longMessage === 'string' ? item.longMessage : null,
        }))
      : [],
  };
}

export function normalizeOAuthRedirectIntentUrl(
  redirectUrlComplete: string,
  currentOrigin = typeof window !== 'undefined' ? window.location.origin : null,
): string | null {
  const candidate = redirectUrlComplete.trim();
  if (!candidate) {
    return null;
  }

  if (candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }

  if (!currentOrigin) {
    return null;
  }

  try {
    const trustedOrigin = new URL(currentOrigin).origin;
    const parsed = new URL(candidate);
    if (
      (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      || parsed.origin !== trustedOrigin
    ) {
      return null;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return null;
  }
}

export function persistOAuthRedirectIntent(intent: OAuthRedirectIntent) {
  if (typeof window === 'undefined') {
    return;
  }

  const redirectUrlComplete = normalizeOAuthRedirectIntentUrl(intent.redirectUrlComplete);
  if (!redirectUrlComplete) {
    console.warn('[auth] Refused to persist an unsafe OAuth redirect intent');
    return;
  }

  try {
    window.sessionStorage.setItem(OAUTH_REDIRECT_INTENT_STORAGE_KEY, JSON.stringify({
      ...intent,
      redirectUrlComplete,
    }));
  } catch (error) {
    console.warn('[auth] Failed to persist OAuth redirect intent', error);
  }
}

export function readOAuthRedirectIntent(): OAuthRedirectIntent | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawIntent = window.sessionStorage.getItem(OAUTH_REDIRECT_INTENT_STORAGE_KEY);
    if (!rawIntent) {
      return null;
    }

    const intent = JSON.parse(rawIntent) as Partial<OAuthRedirectIntent>;
    const redirectUrlComplete = typeof intent.redirectUrlComplete === 'string'
      ? normalizeOAuthRedirectIntentUrl(intent.redirectUrlComplete)
      : null;
    const isValidMode = intent.mode === 'sign-in' || intent.mode === 'sign-up';
    const isRecent =
      typeof intent.createdAt === 'number' &&
      Date.now() - intent.createdAt >= 0 &&
      Date.now() - intent.createdAt <= OAUTH_REDIRECT_INTENT_TTL_MS;

    if (!redirectUrlComplete || !isValidMode || !isRecent) {
      window.sessionStorage.removeItem(OAUTH_REDIRECT_INTENT_STORAGE_KEY);
      return null;
    }

    return {
      mode: intent.mode,
      redirectUrlComplete,
      createdAt: intent.createdAt,
    };
  } catch (error) {
    console.warn('[auth] Failed to read OAuth redirect intent', error);
    window.sessionStorage.removeItem(OAUTH_REDIRECT_INTENT_STORAGE_KEY);
    return null;
  }
}

export function GoogleOAuthButton({
  language,
  mode,
  oauthMode = 'sign-in',
  redirectUrlComplete,
  className = '',
  forceAccountSelection = false,
}: GoogleOAuthButtonProps) {
  const { isLoaded: isSignInLoaded, signIn } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isLoaded = oauthMode === 'sign-up' ? isSignUpLoaded : isSignInLoaded;
  const isDisabled = !isLoaded || isRedirecting;
  const shouldForceAccountSelection = forceAccountSelection || mode === 'sign-up';

  const copy = useMemo(
    () => {
      const isSignUp = mode === 'sign-up';

      return {
        cta:
          language === 'en'
            ? isSignUp
              ? 'Sign up with Google'
              : 'Log in with Google'
            : isSignUp
              ? 'Crear cuenta con Google'
              : 'Iniciar sesión con Google',
        loading: language === 'en' ? 'Connecting to Google…' : 'Conectando con Google…',
        iconLabel: language === 'en' ? 'Google logo' : 'Logo de Google',
      };
    },
    [language, mode],
  );

  const handleClick = useCallback(async () => {
    if (!isLoaded || isRedirecting) {
      return;
    }

    setIsRedirecting(true);
    persistOAuthRedirectIntent({ mode, redirectUrlComplete, createdAt: Date.now() });

    try {
      console.info('[auth] starting interactive Google OAuth', {
        mode,
        oauthMode,
        accountSelection: shouldForceAccountSelection ? 'required' : 'default',
      });

      if (oauthMode === 'sign-up') {
        if (!signUp) {
          throw new Error('Clerk sign-up resource is not ready');
        }
        await startGoogleOAuthRedirect(
          signUp as unknown as LegacyOAuthResource,
          redirectUrlComplete,
          shouldForceAccountSelection,
        );
      } else {
        if (!signIn) {
          throw new Error('Clerk sign-in resource is not ready');
        }
        await startGoogleOAuthRedirect(
          signIn as unknown as LegacyOAuthResource,
          redirectUrlComplete,
          shouldForceAccountSelection,
        );
      }
    } catch (error) {
      console.error(`[auth] Google OAuth redirect failed ${JSON.stringify(describeClerkOAuthError(error))}`);
      setIsRedirecting(false);
    }
  }, [isLoaded, isRedirecting, mode, oauthMode, redirectUrlComplete, shouldForceAccountSelection, signIn, signUp]);

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      disabled={isDisabled}
      className={
        `auth-google-button group inline-flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-200/90 ` +
        `bg-white px-4 text-sm font-semibold leading-none text-slate-800 shadow-[0_14px_34px_rgba(15,23,42,0.2)] ` +
        `transition-[background-color,box-shadow,border-color,color] duration-150 ` +
        `hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:shadow-[0_14px_34px_rgba(15,23,42,0.24)] ` +
        `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ` +
        `disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-600 disabled:shadow-[0_8px_20px_rgba(15,23,42,0.16)] ${className}`
      }
    >
      <svg
        viewBox="0 0 48 48"
        aria-hidden="true"
        role="img"
        className="h-[18px] w-[18px] shrink-0"
        focusable="false"
      >
        <title>{copy.iconLabel}</title>
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.239 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.153 7.959 3.041l5.657-5.657C34.053 6.053 29.281 4 24 4 12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20c0-1.341-.138-2.65-.389-3.917Z" />
        <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.153 7.959 3.041l5.657-5.657C34.053 6.053 29.281 4 24 4 16.318 4 9.656 8.337 6.306 14.691Z" />
        <path fill="#4CAF50" d="M24 44c5.179 0 9.868-1.977 13.409-5.192l-6.19-5.238C29.146 35.091 26.715 36 24 36c-5.218 0-9.621-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44Z" />
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.091 5.571l.003-.002 6.19 5.238C36.971 39.202 44 34 44 24c0-1.341-.138-2.65-.389-3.917Z" />
      </svg>
      <span className="auth-google-button__label">{isRedirecting ? copy.loading : copy.cta}</span>
    </button>
  );
}
